import type { FC } from "react";

import type { SourceControlRepository } from "../../types";

import { FilterableList } from "../common/FilterableList";
import { IndexFreshnessBadge } from "../common/IndexFreshnessBadge";
import { ListItemCard, ListItemCardHeader } from "../common/ListItemCard";
import { PanelHeaderBlock } from "../common/PanelHeaderBlock";
import { PanelSection } from "../common/PanelSection";

interface Props {
  loading: boolean;
  repositories: SourceControlRepository[];
  selectedRepositorySlug: string | null;
  onSelect: (slug: string | null) => void;
}

export const IndexingRepositoryList: FC<Props> = ({
  loading,
  repositories,
  selectedRepositorySlug,
  onSelect,
}) => (
  <PanelSection
    headerContent={
      <PanelHeaderBlock
        eyebrow="Filters"
        title="Repository Scope"
        description="Optionally narrow insights to a specific repository."
      />
    }
  >
    <FilterableList
      items={repositories}
      loading={loading}
      searchPlaceholder="Search repositories..."
      selectedId={selectedRepositorySlug}
      maxHeight="max-h-[20rem]"
      keyExtractor={(repo) => repo.remote_slug}
      filterPredicate={(repo, query) =>
        repo.remote_slug.toLowerCase().includes(query.toLowerCase())
      }
      emptyTitle="No repositories found"
      emptyDescription="Try adjusting your search."
      headerItem={
        <ListItemCard
          clickable
          selected={selectedRepositorySlug === null}
          onClick={() => onSelect(null)}
          padding="md"
        >
          <ListItemCardHeader
            title="All Repositories"
            subtitle="Use run-wide combined insights"
          />
        </ListItemCard>
      }
      renderItem={(repo, isSelected) => {
        const actionsNode =
          repo.index_freshness || repo.last_indexed_at ? (
            <IndexFreshnessBadge
              indexFreshness={repo.index_freshness}
              lastIndexedAt={repo.last_indexed_at}
              className="text-xs"
            />
          ) : undefined;

        return (
          <ListItemCard
            clickable
            selected={isSelected}
            onClick={() => onSelect(repo.remote_slug)}
            padding="md"
            actions={actionsNode}
          >
            <ListItemCardHeader
              title={
                <span className="block max-w-[180px] truncate">
                  {repo.remote_slug}
                </span>
              }
              subtitle={repo.provider_id}
            />
          </ListItemCard>
        );
      }}
    />
  </PanelSection>
);
